import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Box, Grid, Typography } from 'transfix-ui/core'
import { IMotorCarrierEntityTypesEnum } from 'types/FMSGraphqlTypes'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { IBcaInformation, TBcaSteps, IStep1 } from 'apps/fleet-planner/components/activation/types'
import { useActivationContext } from 'apps/fleet-planner/context'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useActivationPageHighwayQuery } from 'apps/fleet-planner/pages/activation/graphql/ActivationPageHighway'
import { getHighwayCarrierInfo } from 'apps/fleet-planner/utils/getHighwayCarrierInfo'
import { Spinner } from 'transfix-ui'
import { postMessageToMobileApp } from 'utils/mobileApp'
import { CompanyInformationForm } from './CompanyInformationForm'
import { CompanyInformationFormPreFilled } from './CompanyInformationFormPreFilled'
import { SignBcaAgreementForm } from './SignBcaAgreementForm'
import { TSignBcaAgreementSchema } from './SignBcaAgreementForm/SignBcaAgreementSchema'
import { EActivationStep, EMobileMessage } from './constants'
import { useCreateMotorCarrierBcaMutation } from './graphql/CreateMotorCarrierBca'
import { useUpdateMotorCarrierCompanyInformationMutation } from './graphql/UpdateMotorCarrierCompanyInformation'

export interface IBrokerCarrierAgreement {
  /**
   * @note optional prop; only used for fmsBcaFormSplit feature
   */
  bcaPart?: number
  /**
   * @note optional prop; only used for fmsBcaFormSplit feature
   */
  setBcaPart?: (value: TBcaSteps) => void

  setBcaInformation?: Dispatch<SetStateAction<IBcaInformation>>
  bcaInformation?: IBcaInformation
}

export const BrokerCarrierAgreementForm = ({
  bcaPart = 1,
  bcaInformation,
  setBcaPart = () => null,
  setBcaInformation = () => null,
}: IBrokerCarrierAgreement) => {
  const { trackEvent } = useFMSAnalytics()
  const isHighwayOnboardingChangeEnabled = useFeatureFlag('fmsOnboardingForm')

  useEffect(() => {
    trackEvent('Setup Guide', 'VIEWED_BCA')
  }, [trackEvent])

  const { carrierDetails, isLoading, mobileApp, setCurrentStep } = useActivationContext()

  const [formErrors, setFormErrors] = useState<string>('')
  const { fleetPlannerMotorCarrierId = '' } = carrierDetails || {}
  const [
    updateMotorCarrierCompanyInformation,
    { loading: loadingCompanyInformation },
  ] = useUpdateMotorCarrierCompanyInformationMutation()
  const [
    createMotorCarrierBca,
    { loading: loadingBcaMutation },
  ] = useCreateMotorCarrierBcaMutation()
  const { step1 } = bcaInformation || {}
  const {
    address,
    addressLine2,
    city,
    contactEmail = '',
    contactPhone,
    countryCode,
    ein = '',
    entityType = '',
    stateCode,
    zipcode,
  } = step1 || {}

  const { data, loading } = useActivationPageHighwayQuery({
    variables: { motorCarrierId: fleetPlannerMotorCarrierId },
  })

  const highwayCarrierInfo = getHighwayCarrierInfo(data)
  const {
    address: highwayAddress1,
    address2: highwayAddress2,
    city: highwayCity,
    countryCode: highwayCountryCode,
    emailAddress,
    highwayId,
    isHighwayInfoComplete,
    phone,
    state: highwayStateCode,
    zipcode: highwayPostalCode,
  } = highwayCarrierInfo || {}

  useEffect(() => {
    if (fleetPlannerMotorCarrierId && !isHighwayInfoComplete && !loading) {
      trackEvent('Setup Guide', 'MISSING_HIGHWAY_DATA')
    }
    // trackEvent can be unstable, not included in dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHighwayInfoComplete, fleetPlannerMotorCarrierId, loading])

  const mutationIsLoading = loadingBcaMutation || loadingCompanyInformation

  const highwayInfoDeterminate = isHighwayOnboardingChangeEnabled
    ? isHighwayInfoComplete
    : highwayAddress1

  const showCarrierInfo = highwayId && highwayInfoDeterminate && fleetPlannerMotorCarrierId

  const step1SuccessHandler = (data: IStep1) => {
    setBcaInformation(prevState => ({ ...prevState, step1: data }))
    setBcaPart(2)
  }

  const backHandler = () => {
    setBcaPart(1)
  }

  const step2SuccessHandler = async (controlData: TSignBcaAgreementSchema) => {
    const { signatory } = controlData || {}

    setBcaInformation(prevState => ({ ...prevState, step2: controlData }))

    const location = showCarrierInfo
      ? {
          address: highwayAddress1,
          addressLine2: highwayAddress2,
          city: highwayCity,
          stateCode: highwayStateCode,
          countryCode: highwayCountryCode,
          zipcode: highwayPostalCode,
          contactEmail: emailAddress,
          contactPhone: phone,
        }
      : {
          address,
          addressLine2,
          city,
          stateCode,
          countryCode,
          zipcode,
          contactEmail,
          contactPhone,
        }

    const { data } = await updateMotorCarrierCompanyInformation({
      variables: {
        input: {
          motorCarrierId: fleetPlannerMotorCarrierId,
          entityType: entityType as IMotorCarrierEntityTypesEnum,
          ein,
          location,
        },
      },
    })

    const { data: bcaData } = await createMotorCarrierBca({
      variables: {
        input: {
          brokerCarrierSignatory: signatory,
          motorCarrierId: fleetPlannerMotorCarrierId,
          contactEmail: emailAddress || contactEmail,
        },
      },
    })

    const { createMotorCarrierBca: signBca } = bcaData || {}

    const { errors: createBcaErrors, motorCarrier: bcaMotorCarrier } = signBca || {}
    const { id: bcaMotorCarrierId } = bcaMotorCarrier || {}

    const { updateMotorCarrierCompanyInformation: companyInformaton } = data || {}
    const { errors: motorCarrierCompanyErrors, motorCarrier: companyInformatonMotorCarrier } =
      companyInformaton || {}
    const { id: companyInformatonMotorCarrierId } = companyInformatonMotorCarrier || {}

    if (createBcaErrors?.length) {
      trackEvent('Setup Guide', 'SUBMIT_BCA_FAILURE', { errors: createBcaErrors })
      setFormErrors(createBcaErrors[0].message || 'Something went wrong. Please try again later.')
      return
    }

    if (motorCarrierCompanyErrors?.length) {
      trackEvent('Setup Guide', 'SUBMIT_BCA_FAILURE', { errors: motorCarrierCompanyErrors })
      setFormErrors(
        motorCarrierCompanyErrors[0].message || 'Something went wrong. Please try again later.'
      )
      return
    }

    if (companyInformatonMotorCarrierId && bcaMotorCarrierId) {
      trackEvent('Setup Guide', 'SUBMIT_BCA_SUCCESS')
      postMessageToMobileApp(mobileApp, EMobileMessage.COMPANY_INFO_COLLECTED)
      setCurrentStep(EActivationStep.insurance)
    } else {
      trackEvent('Setup Guide', 'SUBMIT_BCA_FAILURE')
      setFormErrors('Something went wrong. Please try again later.')
    }
  }

  const headerTitle = highwayId ? 'Verify Company Info' : `Please Enter Company Info.`

  if (loading || isLoading || !fleetPlannerMotorCarrierId) {
    return (
      <div>
        <Spinner />
      </div>
    )
  }

  return (
    <div>
      <Grid container alignItems='center' justifyContent='center'>
        <Grid item xs={12} md={8} lg={6}>
          {bcaPart === 1 && (
            <>
              <Box mb={2.5} mt={1.5}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant='h3'>{headerTitle}</Typography>
                  </Grid>
                </Grid>
              </Box>
              {showCarrierInfo ? (
                <CompanyInformationFormPreFilled
                  onSubmitSuccess={step1SuccessHandler}
                  defaultValues={step1}
                />
              ) : (
                <CompanyInformationForm
                  onSubmitSuccess={step1SuccessHandler}
                  defaultValues={step1}
                />
              )}
            </>
          )}

          {bcaPart === 2 && (
            <SignBcaAgreementForm
              onSubmitSuccess={step2SuccessHandler}
              ein={ein}
              entity={entityType}
              backHandler={backHandler}
              isLoading={isLoading}
              mutationIsLoading={mutationIsLoading}
              carrierDetails={carrierDetails}
              formErrors={formErrors}
            />
          )}
        </Grid>
      </Grid>
    </div>
  )
}
